import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import "./AdminShared.css";

export default function JobPostings() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    title: "",
    department: "",
    location: "",
    type: "",
    mode: "",
    experience: "",
    salary: "",
    qualifications: "",
    description: "",
    vacancies: "",
  });

  const stored = JSON.parse(localStorage.getItem("adminInfo") || "null");
  const token = stored?.token;

  /* ----------------------------------
     FETCH JOB POSTINGS
  ---------------------------------- */
  const fetchJobs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/job-postings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setJobs(Array.isArray(data) ? data.reverse() : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return navigate("/admin");
    fetchJobs();
  }, [fetchJobs, navigate, token]);

  /* ----------------------------------
     HANDLE FORM CHANGE
  ---------------------------------- */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /* ----------------------------------
     CREATE JOB
  ---------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      qualifications: form.qualifications
        .split("\n")
        .map((q) => q.trim())
        .filter(Boolean),
      description: form.description
        .split("\n")
        .map((d) => d.trim())
        .filter(Boolean),
    };

    const url = editingId
      ? `${API_BASE_URL}/admin/job-postings/${editingId}`
      : `${API_BASE_URL}/admin/job-postings`;
    const method = editingId ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      alert(
        editingId
          ? "Opportunity updated successfully."
          : "New opportunity broadcasted.",
      );
      setForm({
        title: "",
        department: "",
        location: "",
        type: "",
        mode: "",
        experience: "",
        salary: "",
        qualifications: "",
        description: "",
        vacancies: "",
      });
      setEditingId(null);
      setShowForm(false);
      fetchJobs();
    }
  };

  /* ----------------------------------
     DELETE JOB
  ---------------------------------- */
  const deleteJob = async (id) => {
    if (!window.confirm("Purge this vacancy?")) return;
    await fetch(`${API_BASE_URL}/admin/job-postings/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchJobs();
  };

  const editJob = (job) => {
    setEditingId(job._id);
    setForm({
      title: job.title || "",
      department: job.department || "",
      location: job.location || "",
      type: job.type || "",
      mode: job.mode || "",
      experience: job.experience || "",
      salary: job.salary || "",
      qualifications: (job.qualifications || []).join("\n"),
      vacancies: job.vacancies || "",
      description: Array.isArray(job.description)
        ? job.description.join("\n")
        : job.description || "",
    });
    setShowForm(true);
  };

  const toggleJobStatus = async (id) => {
    await fetch(`${API_BASE_URL}/admin/job-postings/${id}/status`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchJobs();
  };

  if (loading)
    return (
      <div className="admin-list-container">
        <p style={{ padding: "100px", textAlign: "center" }}>
          ⏳ Synchronizing Career Data...
        </p>
      </div>
    );

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <button
            className="admin-back-btn"
            onClick={() => navigate("/admin/panel")}
          >
            ←
          </button>
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">Career Manager</h1>
        </div>
        <button
          className="module-badge"
          style={{ cursor: "pointer", background: "#ec4899", border: "none" }}
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) setEditingId(null);
          }}
        >
          {showForm ? "View Openings" : "+ Create Opportunity"}
        </button>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: "#ec4899" }}></div>
            Context: Global Hiring Node
          </div>
          <div
            className="admin-status-badge"
            style={{
              background: "#fdf2f8",
              color: "#be185d",
              fontSize: "0.75rem",
              fontWeight: 800,
            }}
          >
            {jobs.length} ACTIVE LISTINGS
          </div>
        </div>

        {showForm ? (
          <div
            className="admin-table-wrapper"
            style={{ padding: "40px", background: "#fff" }}
          >
            <h2 style={{ marginBottom: "30px", fontWeight: 800 }}>
              {editingId ? "Modify Opportunity" : "Provision New Vacancy"}
            </h2>
            <form
              className="admin-form-elite"
              style={{ padding: 0, border: "none", boxShadow: "none" }}
              onSubmit={handleSubmit}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "30px",
                }}
              >
                <label>
                  Engagement Title{" "}
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    required
                  />
                </label>
                <label>
                  Operational Unit{" "}
                  <input
                    name="department"
                    value={form.department}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Geographic Hub{" "}
                  <input
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Engagement Type{" "}
                  <input
                    name="type"
                    placeholder="Full-time / Part-time"
                    value={form.type}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Work Modality{" "}
                  <input
                    name="mode"
                    placeholder="Remote / Hybrid / Onsite"
                    value={form.mode}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Seniority Level{" "}
                  <input
                    name="experience"
                    value={form.experience}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Vacancies
                  <input
                    name="vacancies"
                    type="number"
                    value={form.vacancies}
                    onChange={handleChange}
                    placeholder="Number of openings"
                  />
                </label>
              </div>
              <div
                style={{
                  marginTop: "30px",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "30px",
                }}
              >
                <label>
                  Compensation Package{" "}
                  <input
                    name="salary"
                    value={form.salary}
                    onChange={handleChange}
                  />
                </label>
                <label>
                  Primary Qualifications{" "}
                  <textarea
                    name="qualifications"
                    value={form.qualifications}
                    onChange={handleChange}
                    style={{ height: "150px" }}
                  />
                </label>
              </div>
              <div style={{ marginTop: "30px" }}>
                <label>
                  Detailed Scope of Work (One point per line)
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    style={{ height: "150px" }}
                  />
                </label>
              </div>
              <div style={{ marginTop: "40px", display: "flex", gap: "15px" }}>
                <button
                  className="btn-primary-elite"
                  type="submit"
                  style={{ width: "200px" }}
                >
                  {editingId ? "Update Vacancy" : "Broadcast Vacancy"}
                </button>
                <button
                  className="btn-update"
                  type="button"
                  style={{ background: "#f1f5f9", border: "none" }}
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="elite-table">
              <thead>
                <tr>
                  <th>Designation</th>
                  <th>Classification</th>
                  <th>Status</th>
                  <th>Performance Node</th>
                  <th>Operations</th>
                  <th>Vacancies</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      style={{
                        textAlign: "center",
                        padding: "100px",
                        color: "#94a3b8",
                      }}
                    >
                      No career opportunities are currently provisioned.
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                    <tr key={job._id}>
                      <td>
                        <div style={{ fontWeight: 700 }}>{job.title}</div>
                        <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                          {job.department} • {job.location}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: "0.9rem", color: "#475569" }}>
                          {job.type}
                        </div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: "#ec4899",
                          }}
                        >
                          {job.mode.toUpperCase()}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`status-tag status-${job.status === "active" ? "approved" : "pending"}`}
                        >
                          {job.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: "0.85rem" }}>
                          {job.experience}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                          Est. {job.salary}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            className="action-btn-elite btn-update"
                            onClick={() => editJob(job)}
                          >
                            <span>✏️</span>
                          </button>
                          <button
                            className="action-btn-elite btn-delete"
                            onClick={() => deleteJob(job._id)}
                          >
                            <span>🗑️</span>
                          </button>
                          <button
                            className="action-btn-elite"
                            style={{
                              background:
                                job.status === "active" ? "#fee2e2" : "#d1fae5",
                              color:
                                job.status === "active" ? "#ef4444" : "#10b981",
                            }}
                            onClick={() => toggleJobStatus(job._id)}
                          >
                            {job.status === "active" ? "CLOSE" : "ACTIVATE"}
                          </button>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: "0.85rem" }}>
                          {job.vacancies}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
